import { useThemeContextSelector } from "@/utils/theme";
import Box from "@mui/material/Box";
import React from "react";

export type ButtonVariant = "primary" | "ghost";
export type ButtonSize = "medium" | "small" | "icon" | "iconSmall";

export type ButtonProps = {
    variant?: ButtonVariant;
    active?: boolean;
    buttonSize?: ButtonSize;
} & React.ButtonHTMLAttributes<HTMLButtonElement>;

export const Button = ({
    ref,
    active,
    buttonSize = "medium",
    children,
    disabled,
    variant = "primary",
    style,
    ...rest
}: ButtonProps & {
    ref?: React.RefObject<HTMLButtonElement>;
}) => {
    const isDarkMode = useThemeContextSelector(state => state.isDarkMode);

    const buttonStyle = {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        border: "1px solid transparent",
        gap: "8px",
        fontSize: "14px",
        fontWeight: 600,
        borderRadius: "6px",
        whiteSpace: "nowrap",
        opacity: disabled ? 0.5 : 1,
        ...(variant === "primary" && {
            color: isDarkMode ? "black" : "white",
            backgroundColor: isDarkMode ? "white" : "black",
            borderColor: isDarkMode ? "white" : "black",
            ...(!disabled &&
                !active && {
                    "&:hover": {
                        backgroundColor: isDarkMode ? "#e5e5e5" : "#262626",
                    },
                    "&:active": {
                        backgroundColor: isDarkMode ? "#d4d4d4" : "#171717",
                    },
                }),
            ...(active && {
                backgroundColor: isDarkMode ? "#d4d4d4" : "#171717",
            }),
        }),
        ...(variant === "ghost" && {
            backgroundColor: "transparent",
            borderColor: "transparent",
            color: isDarkMode ? "#a3a3a3" : "#737373",
            ...(!disabled &&
                !active && {
                    "&:hover": {
                        backgroundColor: isDarkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.05)",
                        color: isDarkMode ? "#d4d4d4" : "#404040",
                    },
                    "&:active": {
                        backgroundColor: isDarkMode ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.1)",
                        color: isDarkMode ? "#e5e5e5" : "#262626",
                    },
                }),
            ...(active && {
                backgroundColor: isDarkMode ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.1)",
                color: isDarkMode ? "#e5e5e5" : "#262626",
            }),
        }),
        ...(buttonSize === "medium" && {
            padding: "8px 12px",
        }),
        ...(buttonSize === "small" && {
            padding: "4px 8px",
        }),
        ...(buttonSize === "icon" && {
            width: "32px",
            height: "32px",
        }),
        cursor: disabled ? "not-allowed" : "pointer",
        ...style,
    };

    return (
        <Box component="button" ref={ref} sx={buttonStyle} {...rest}>
            {children}
        </Box>
    );
};

Button.displayName = "Button";
